import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

const BUTTON_VARIANTS = {
  primary: 'bg-primary hover:bg-primary-dark text-white dark:bg-primary dark:hover:bg-primary-dark shadow-button',
  secondary: 'bg-secondary hover:bg-secondary-dark text-white dark:bg-secondary dark:hover:bg-secondary-dark',
  outline: 'bg-transparent text-foreground shadow-[inset_0_0_0_1px_var(--border)] hover:bg-background-light dark:text-foreground',
  ghost: 'bg-transparent text-muted-foreground hover:bg-background-light hover:text-foreground dark:text-muted-foreground dark:hover:bg-background-light',
  error: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
  success: 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-600',
  info: 'bg-primary text-white hover:bg-primary-dark dark:bg-primary dark:hover:bg-primary-dark',
  remove: 'bg-red-600 hover:bg-red-700 text-white dark:bg-red-700 dark:hover:bg-red-800',
  disabled: 'bg-background-light text-muted-foreground cursor-not-allowed',
  iconButton: 'p-1 text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed'
};

const BUTTON_SIZES = {
  sm: 'px-2 py-1 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-3 text-base'
};

const Button = forwardRef(({ variant = 'primary', size = 'md', className = '', disabled = false, ...props }, ref) => {
  if (!Object.prototype.hasOwnProperty.call(BUTTON_VARIANTS, variant)) {
    throw new Error(`Unknown Button variant: ${variant}`);
  }

  if (!Object.prototype.hasOwnProperty.call(BUTTON_SIZES, size)) {
    throw new Error(`Unknown Button size: ${size}`);
  }

  const baseClasses = 'inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold transition-colors duration-150 focus:outline-none focus:ring-4 focus:ring-primary/15';
  const variantClasses = disabled ? BUTTON_VARIANTS.disabled : BUTTON_VARIANTS[variant];
  const sizeClasses = BUTTON_SIZES[size];
  const disabledClasses = disabled ? 'opacity-70 cursor-not-allowed' : '';

  return (
    <button
      type="button"
      className={`${baseClasses} ${variantClasses} ${sizeClasses} ${disabledClasses} ${className}`}
      disabled={disabled}
      ref={ref}
      {...props}
    >
      {props.children}
    </button>
  );
});

Button.displayName = 'Button';

Button.propTypes = {
  variant: PropTypes.oneOf(Object.keys(BUTTON_VARIANTS)),
  size: PropTypes.oneOf(Object.keys(BUTTON_SIZES)),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node
};

export { Button };
